import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Container,
    Group,
    Modal,
    Paper,
    Skeleton,
    Stack,
    Table,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconArrowLeft, IconPlayerPlay, IconShare, IconTrash } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { formatDate, formatRecordingDuration, formatSize } from './format';
import { RecordingPlayer } from './recording-player';
import { type Recording, type RecordingList, recordingsApi } from './recordings-api';
import { ShareModal } from './share-modal';
import { i18n } from '@/i18n';
import { LanguageSwitcher } from '@/shared/language-switcher';
import { UserMenu } from '@/shared/user-menu';
import { notifyError } from '@/shared/notify';

const STATUS_COLORS = { starting: 'yellow', active: 'red', failed: 'gray' } as const;

export function RecordingsPage() {
    const { t } = useTranslation('recordings');
    const language = i18n.language;
    const [list, setList] = useState<RecordingList | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [playing, setPlaying] = useState<Recording | null>(null);
    const [sharing, setSharing] = useState<Recording | null>(null);
    const [confirmingDelete, setConfirmingDelete] = useState<Recording | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        recordingsApi
            .list()
            .then((loaded) => {
                if (!cancelled) setList(loaded);
            })
            .catch((error_: unknown) => {
                if (cancelled) return;
                setError(error_ instanceof Error ? error_.message : i18n.t('recordings:errors.unknown'));
                notifyError(i18n.t('recordings:errors.load'), error_);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const loadPlayingUrl = useMemo(
        () => (playing ? (download?: boolean) => recordingsApi.url(playing.id, download) : null),
        [playing]
    );

    const deleteRecording = async (recording: Recording) => {
        setDeleting(true);
        try {
            await recordingsApi.remove(recording.id);
            setList(
                (current) => current && { ...current, recordings: current.recordings.filter((item) => item.id !== recording.id) }
            );
            setConfirmingDelete(null);
            notifications.show({ message: t('deleted') });
        } catch (error_) {
            notifyError(t('errors.delete'), error_);
        } finally {
            setDeleting(false);
        }
    };

    const renderContent = () => {
        if (error) {
            return (
                <Alert color="danger" icon={<IconAlertTriangle size={20} />} title={t('errors.load')}>
                    {error}
                </Alert>
            );
        }
        if (!list) {
            return (
                <Stack gap="sm">
                    <Skeleton h="var(--spacing-10)" />
                    <Skeleton h="var(--spacing-10)" />
                    <Skeleton h="var(--spacing-10)" />
                </Stack>
            );
        }
        if (list.recordings.length === 0) {
            return (
                <Text c="dimmed" py="xl" ta="center">
                    {t('empty')}
                </Text>
            );
        }
        const showRoles = list.recordings.some((recording) => recording.role !== null);
        return (
            <Table.ScrollContainer minWidth={0}>
                <Table highlightOnHover style={{ whiteSpace: 'nowrap' }} verticalSpacing="sm">
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>{t('columns.room')}</Table.Th>
                            <Table.Th visibleFrom="sm">{t('columns.date')}</Table.Th>
                            <Table.Th visibleFrom="sm">{t('columns.duration')}</Table.Th>
                            <Table.Th visibleFrom="sm">{t('columns.size')}</Table.Th>
                            {showRoles && <Table.Th visibleFrom="sm">{t('columns.role')}</Table.Th>}
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {list.recordings.map((recording) => (
                            <Table.Tr key={recording.id}>
                                <Table.Td style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                    <Stack align="flex-start" gap="var(--spacing-1)">
                                        <Text fw={500}>{recording.roomName}</Text>
                                        <Text c="dimmed" hiddenFrom="sm" size="sm">
                                            {[
                                                formatDate(recording.startedAt, language),
                                                recording.durationMs === null
                                                    ? null
                                                    : formatRecordingDuration(recording.durationMs),
                                                recording.sizeBytes ? formatSize(recording.sizeBytes, language) : null,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </Text>
                                        <Group gap="var(--spacing-1)">
                                            {recording.status !== 'complete' && (
                                                <Badge color={STATUS_COLORS[recording.status]} size="sm" variant="light">
                                                    {t(`status.${recording.status}`)}
                                                </Badge>
                                            )}
                                            {recording.role && (
                                                <Badge
                                                    color={recording.role === 'owner' ? 'violet' : 'gray'}
                                                    hiddenFrom="sm"
                                                    size="sm"
                                                    variant="light"
                                                >
                                                    {t(`role.${recording.role}`)}
                                                </Badge>
                                            )}
                                            {recording.transcribe && (
                                                <Tooltip multiline label={t('transcriptPendingHint')} maw="var(--spacing-80)">
                                                    <Badge
                                                        color="blue"
                                                        data-testid="transcript-pending"
                                                        size="sm"
                                                        variant="light"
                                                    >
                                                        {t('transcriptPending')}
                                                    </Badge>
                                                </Tooltip>
                                            )}
                                        </Group>
                                    </Stack>
                                </Table.Td>
                                <Table.Td visibleFrom="sm">{formatDate(recording.startedAt, language)}</Table.Td>
                                <Table.Td visibleFrom="sm">
                                    <Text c="dimmed">{formatRecordingDuration(recording.durationMs)}</Text>
                                </Table.Td>
                                <Table.Td visibleFrom="sm">
                                    <Text c="dimmed">
                                        {recording.sizeBytes ? formatSize(recording.sizeBytes, language) : '—'}
                                    </Text>
                                </Table.Td>
                                {showRoles && (
                                    <Table.Td visibleFrom="sm">
                                        {recording.role && (
                                            <Badge color={recording.role === 'owner' ? 'violet' : 'gray'} variant="light">
                                                {t(`role.${recording.role}`)}
                                            </Badge>
                                        )}
                                    </Table.Td>
                                )}
                                <Table.Td>
                                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                                        <Button
                                            disabled={recording.status !== 'complete'}
                                            leftSection={<IconPlayerPlay size={16} />}
                                            size="sm"
                                            variant="light"
                                            visibleFrom="sm"
                                            onClick={() => {
                                                setPlaying(recording);
                                            }}
                                        >
                                            {t('watch')}
                                        </Button>
                                        <ActionIcon
                                            aria-label={t('watch')}
                                            disabled={recording.status !== 'complete'}
                                            hiddenFrom="sm"
                                            variant="light"
                                            onClick={() => {
                                                setPlaying(recording);
                                            }}
                                        >
                                            <IconPlayerPlay size={16} />
                                        </ActionIcon>
                                        {recording.role === 'owner' && (
                                            <>
                                                <Tooltip label={t('share')}>
                                                    <ActionIcon
                                                        aria-label={t('share')}
                                                        disabled={recording.status !== 'complete'}
                                                        size="sm"
                                                        variant="default"
                                                        onClick={() => {
                                                            setSharing(recording);
                                                        }}
                                                    >
                                                        <IconShare size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                                <Tooltip label={t('delete')}>
                                                    <ActionIcon
                                                        aria-label={t('delete')}
                                                        color="danger"
                                                        size="sm"
                                                        variant="subtle"
                                                        disabled={
                                                            recording.status === 'starting' || recording.status === 'active'
                                                        }
                                                        onClick={() => {
                                                            setConfirmingDelete(recording);
                                                        }}
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
        );
    };

    return (
        <Container py="xl" size="lg">
            <Stack gap="lg">
                <Group justify="space-between">
                    <Button component={Link} leftSection={<IconArrowLeft size={20} />} to="/" variant="subtle">
                        {t('back')}
                    </Button>
                    <Group gap="var(--spacing-2)" wrap="nowrap">
                        <LanguageSwitcher />
                        <UserMenu />
                    </Group>
                </Group>
                <Title order={2}>{t('title')}</Title>
                <Paper withBorder p={{ base: 0, sm: 'md' }}>
                    {renderContent()}
                </Paper>
            </Stack>

            <Modal
                opened={playing !== null}
                size="xl"
                title={playing ? `${playing.roomName} — ${formatDate(playing.startedAt, language)}` : undefined}
                onClose={() => {
                    setPlaying(null);
                }}
            >
                {loadPlayingUrl && <RecordingPlayer loadUrl={loadPlayingUrl} />}
            </Modal>

            {sharing && list && (
                <ShareModal
                    publicLinksEnabled={list.publicLinksEnabled}
                    recording={sharing}
                    onClose={() => {
                        setSharing(null);
                    }}
                />
            )}

            <Modal
                centered
                opened={confirmingDelete !== null}
                title={t('deleteConfirm.title')}
                onClose={() => {
                    setConfirmingDelete(null);
                }}
            >
                {confirmingDelete && (
                    <Stack gap="lg">
                        <Text size="sm">
                            {t('deleteConfirm.message', {
                                room: confirmingDelete.roomName,
                                date: formatDate(confirmingDelete.startedAt, language),
                            })}
                        </Text>
                        <Group gap="sm" justify="flex-end">
                            <Button
                                variant="default"
                                onClick={() => {
                                    setConfirmingDelete(null);
                                }}
                            >
                                {t('deleteConfirm.cancel')}
                            </Button>
                            <Button color="danger" loading={deleting} onClick={() => void deleteRecording(confirmingDelete)}>
                                {t('deleteConfirm.confirm')}
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>
        </Container>
    );
}
